/** The prototype's recording still + Play overlay, shared by React and guest comment rows. */
import { PlayIcon } from '@hugeicons/core-free-icons';
import { FOUNDATIONS } from '../designFoundations';
import { DESIGN_RECIPES } from '../designRecipes';
import { PALETTE } from '../designPalette';
import { createIcon } from './iconDom';
/** Decode a small still, then release the video bytes. No full recording is retained in a thumbnail cache. */
export async function recordingPoster(load, signal, doc = document) {
    const file = await load();
    if (signal.aborted)
        throw new DOMException('Preview closed', 'AbortError');
    const bytes = Uint8Array.from(atob(file.base64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
    return new Promise((resolve, reject) => {
        const video = doc.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        const cleanup = () => {
            clearTimeout(timer);
            signal.removeEventListener('abort', abort);
            video.onloadeddata = video.onseeked = video.onerror = null;
            video.pause();
            video.removeAttribute('src');
            video.load();
            URL.revokeObjectURL(url);
        };
        const fail = (error) => { cleanup(); reject(error); };
        const abort = () => fail(new DOMException('Preview closed', 'AbortError'));
        const timer = setTimeout(() => fail(new Error('Recording preview timed out')), 10_000);
        const draw = () => {
            try {
                if (!video.videoWidth || !video.videoHeight)
                    throw new Error('No recording frame');
                const canvas = doc.createElement('canvas');
                // Bitmap resolution: four times the shipped thumbnail slot, independent of the recording's resolution.
                const scale = Math.min(parseFloat(FOUNDATIONS.space[10]) * 4 / video.videoWidth, parseFloat(FOUNDATIONS.space[8]) * 4 / video.videoHeight);
                canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
                canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
                const ctx = canvas.getContext('2d');
                if (!ctx)
                    throw new Error('Preview canvas unavailable');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const poster = canvas.toDataURL('image/png');
                cleanup();
                resolve(poster);
            }
            catch (error) {
                fail(error instanceof Error ? error : new Error('Preview unavailable'));
            }
        };
        video.onloadeddata = () => {
            // A short seek avoids an encoder's empty opening frame; very short takes use their first decoded frame.
            if (Number.isFinite(video.duration) && video.duration > 0.2)
                video.currentTime = 0.1;
            else
                draw();
        };
        video.onseeked = draw;
        video.onerror = () => fail(new Error('Recording preview unavailable'));
        signal.addEventListener('abort', abort, { once: true });
        video.src = url;
    });
}
/** Mount into an existing accessible Play button. Loading/failure never disables its playback action. */
export function mountVideoThumbnail(button, load) {
    const doc = button.ownerDocument;
    const controller = new AbortController();
    button.classList.add('oq-video-thumbnail');
    Object.assign(button.style, {
        position: 'relative', display: 'inline-flex', flexShrink: '0', alignItems: 'center', justifyContent: 'center',
        width: FOUNDATIONS.space[10], height: FOUNDATIONS.space[8], padding: '0', border: '0', overflow: 'hidden',
        borderRadius: FOUNDATIONS.radius.sm, background: PALETTE.neutral[100], cursor: 'pointer',
    });
    button.dataset.videoPreview = 'loading';
    const img = doc.createElement('img');
    img.alt = '';
    img.hidden = true;
    Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover' });
    const overlay = doc.createElement('span');
    Object.assign(overlay.style, { position: 'absolute', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE.surface.darkText });
    const icon = createIcon(doc, PlayIcon, 'play');
    Object.assign(icon.style, { boxSizing: 'content-box', width: DESIGN_RECIPES.icon.compact, height: DESIGN_RECIPES.icon.compact, padding: FOUNDATIONS.space[1], borderRadius: FOUNDATIONS.radius.full, background: PALETTE.surface.dark });
    overlay.append(icon);
    button.append(img, overlay);
    let started = false, connected = button.isConnected;
    const start = () => {
        if (started || controller.signal.aborted)
            return;
        started = true;
        void recordingPoster(load, controller.signal, doc).then(src => {
            if (controller.signal.aborted)
                return;
            img.src = src;
            img.hidden = false;
            button.dataset.videoPreview = 'ready';
        }).catch(() => { if (!controller.signal.aborted)
            button.dataset.videoPreview = 'unavailable'; });
    };
    const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
            intersection?.disconnect();
            start();
        }
    }) : null;
    const removed = new MutationObserver(() => {
        if (button.isConnected)
            connected = true;
        else if (connected)
            dispose();
    });
    const dispose = () => {
        controller.abort();
        intersection?.disconnect();
        removed.disconnect();
        img.remove();
        overlay.remove();
    };
    removed.observe(doc.body, { childList: true, subtree: true });
    if (intersection)
        intersection.observe(button);
    else
        start();
    return dispose;
}
//# sourceMappingURL=videoThumbnail.js.map